import { formatMinutesToHoursWithMinutes } from '../../utilities/calendarUtilities';
import debounce from 'lodash/debounce';
import { DEBOUNCE_DELAY, RESERVE_DOWN_TO, LAST_BID_SCOPE_USE, RESERVE_BID_SCOPE, STAFFID } from '../../data/constants';
import { is } from 'date-fns/locale';

// BidMode constants - these should be language-independent constant values
export const BidMode = Object.freeze({
  NONE: { weightingMode: 0, biddingMode: 'NONE', changedWeight: undefined },
  MANUAL: { weightingMode: 1, biddingMode: 'MANUAL', changedWeight: 500 },
  AUTOMATIC: { weightingMode: 2, biddingMode: 'AUTOMATIC', changedWeight: 2 },
  LADDER: { weightingMode: 3, biddingMode: 'LADDER', changedWeight: undefined },
  PRIORITY: { weightingMode: 4, biddingMode: 'PRIORITY', changedWeight: undefined }
});

const getLabels = () => sessionStorage.getItem('lng') === 'fr'
  ? require('../../labels/labels.french.json')
  : require('../../labels/labels.json');

export const OPTION_LEVEL_WEIGHTED_PREFERENCE = 505;

export const getBidMode = function () {
  return sessionStorage.getItem('bidmode');
}

export const saveBidMode = function (bidMode) {
  sessionStorage.setItem('bidmode', bidMode);
};

export const getLastBidScopeUsedString = function () {
  return sessionStorage.getItem(LAST_BID_SCOPE_USE);
};

export function movePreference(type, oldScore, preferences, isCombined) {
  let bidPreferences = preferences;
  const itemPreference = bidPreferences
    .filter(item => item.score === oldScore);
  const idx = bidPreferences.indexOf(itemPreference[0]);
  let condition = false;
  let position = 0;
  let bidAggregateIds = [];
  let bidAggregateIdsToSwitchWith = [];
  if (type === 'down') {
    condition = idx < bidPreferences.length;
    position = idx + 1;
  } else {
    condition = idx > 0;
    position = idx - 1;
  }
  if (condition) {
    const itemToMove = !isCombined ? bidPreferences.splice(idx, 1) : bidPreferences.filter(item => item.groupName === itemPreference[0].groupName);

    !isCombined ? bidAggregateIds.push(itemToMove[0].bidAggregateId) : bidAggregateIds.push(...itemToMove.map(item => item.bidAggregateId));

    if (type === 'down') { // if move preference down
      if (bidPreferences[idx].groupName) { // check if the item under the selected item is combined
        for (let i = idx; i < bidPreferences.length; i++) {
          if (bidPreferences[i].groupName !== itemPreference[0].groupName) { // check if the item under the selected item is not in the same group

            if (bidPreferences[i].score > itemPreference[0].score + 1) { // check if the item under the selected has a score greater than the selected item + 1, so we break
              break
            }
            if (bidPreferences[i].score === itemPreference[0].score + 1) { // check if the item under the selected has a score equal to the selected item + 1, so
              bidAggregateIdsToSwitchWith.push(bidPreferences[i].bidAggregateId)
            }
          }
        }
      }
      else bidAggregateIdsToSwitchWith.push(bidPreferences[idx].bidAggregateId) // if the item under the selected item is not combined, add it to the list of
    }
    else { // if move preference up
      bidPreferences[position].groupName ?
        bidAggregateIdsToSwitchWith.push(...bidPreferences.filter(item => item.groupName === bidPreferences[position].groupName).map(item => item.bidAggregateId)) :
        bidAggregateIdsToSwitchWith.push(bidPreferences[position].bidAggregateId)
    }
  }

  return {
    bidAggregateIds,
    bidAggregateIdsToSwitchWith,
  };
}

export function swapPositionsWithScores(array, oldIndex, newIndex) {
  // Helper to find the block range (start and end index) if part of a group
  function getBlockRange(index) {
    const groupName = array[index].groupName;
    if (!groupName) return { start: index, end: index }; // Single item (no group)

    let start = index;
    let end = index;

    // Expand backward to find the start of the group
    while (start > 0 && array[start - 1].groupName === groupName) {
      start--;
    }

    // Expand forward to find the end of the group
    while (end < array.length - 1 && array[end + 1].groupName === groupName) {
      end++;
    }

    return { start, end };
  }

  // Get ranges for both indices
  const { start: startA, end: endA } = getBlockRange(oldIndex);
  const { start: startB, end: endB } = getBlockRange(newIndex);

  // Extract the blocks to be swapped
  const blockA = array.slice(startA, endA + 1);
  const blockB = array.slice(startB, endB + 1);

  // Swap scores within each block
  const scoreA = blockA[0].score;
  const scoreB = blockB[0].score;

  blockA.forEach(item => (item.score = scoreB));
  blockB.forEach(item => (item.score = scoreA));

  // if moving up
  if (startA > startB) {
    const beforeItem = array[startA - 1]; // Save the item just before the old index

    // Remove both blocks from the array
    array.splice(startA, blockA.length);

    array.splice(array.indexOf(beforeItem) + 1, 0, ...blockB); // Place blockB after `beforeItem`
    array.splice(startB - (startA < startB ? blockA.length : 0), blockB.length); // Remove original blockB

    array.splice(startB, 0, ...blockA); // Place blockA at its new location

  } else {
    // Calculate the lengths
    const lengthA = endA - startA + 1;
    const lengthB = endB - startB + 1;

    // Remove both blocks from the array
    array.splice(startA, lengthA);
    array.splice(startB - (startA < startB ? lengthA : 0), lengthB);

    // Insert blockA into position of blockB
    array.splice(startB - (startA < startB ? lengthA : 0), 0, ...blockA);

    // Insert blockB into position of blockA
    array.splice(startA, 0, ...blockB);
  }

  return array;
}

export function moveItem(array, oldIndex, newIndex) {
  if (oldIndex === newIndex) return [...array]; // No change needed

  function getBlockRange(index) {
    const groupName = array[index]?.groupName;
    if (!groupName) return { start: index, end: index }; // Single item

    let start = index;
    let end = index;

    while (start > 0 && array[start - 1]?.groupName === groupName) start--;
    while (end < array.length - 1 && array[end + 1]?.groupName === groupName) end++;

    return { start, end };
  }

  // Step 1: Get the moving block range (single or combined)
  const { start: startA, end: endA } = getBlockRange(oldIndex);
  const movingBlock = array.slice(startA, endA + 1);

  // Step 2: Remove the moving block
  array.splice(startA, movingBlock.length);

  // Step 3: Find the target block range (where to insert)
  const { start: targetStart, end: targetEnd } = getBlockRange(newIndex > oldIndex ? newIndex - movingBlock.length : newIndex);

  // Step 4: Adjust insertion position to avoid breaking blocks
  const insertPosition = newIndex > oldIndex ? targetEnd + 1 : targetStart;

  // Step 5: Insert the moving block at the correct position
  array.splice(insertPosition, 0, ...movingBlock);

  // Step 6: Recalculate scores properly
  let currentScore = 1;
  const groupedScores = new Map();

  for (let i = 0; i < array.length; i++) {
    if (array[i].groupName) {
      if (groupedScores.has(array[i].groupName)) {
        array[i].score = groupedScores.get(array[i].groupName);
      } else {
        groupedScores.set(array[i].groupName, currentScore);
        array[i].score = currentScore;
      }
    } else {
      array[i].score = currentScore;
    }

    // Increment score if next item is in a different group or last item
    if (i === array.length - 1 || array[i + 1]?.groupName !== array[i]?.groupName || array[i].groupName === null) {
      currentScore++;
    }
  }

  return [...array]; // Ensure React state immutability

}

export function sortPreferences(preferences) {
  // Step 1: Group preferences by `groupName`
  const grouped = new Map();

  preferences.forEach(pref => {
    if (isOptionLevelIsSmallerEqualThanReserve(pref.optionLevel)) {
      pref.score = 0;
    }
    const key = pref.groupName || `single-${pref.bidAggregateId}`;
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key).push(pref);
  });

  // Step 2: Sort within each group by `groupSequence` first, then `bidSequence`
  grouped.forEach((group, key) => {
    group.sort((a, b) => {
      return a.groupSequence - b.groupSequence || a.bidSequence - b.bidSequence;
    });
  });

  // Step 3: Sort groups by lowest `score`, keeping copied groups correctly placed
  const sortedGroups = Array.from(grouped.values()).sort((a, b) => {
    return a[0].score - b[0].score || a[0].groupSequence - b[0].groupSequence;
  });

  // Step 4: Flatten the sorted groups into a single array
  return sortedGroups.flat();
}

export const groupByGroupSequence = (data) => {
  // To track groupSequence
  const groups = new Map();

  data.forEach(item => {
    const groupSeq = item.groupSequence;

    // initiate the group if it doesn't exist
    if (!groups.has(groupSeq)) {
      groups.set(groupSeq, { groupSequence: groupSeq, preferences: [] });
    }

    // add the item to the group
    groups.get(groupSeq).preferences.push(item);
  });

  // Sort preferences within each group by bidSequence
  groups.forEach(group => group.preferences.sort((a, b) => a.bidSequence - b.bidSequence));

  // return the groups as array
  return Array.from(groups.values());
}

export function orderPreferences(bidPreferences) {
  const bids = bidPreferences.slice();
  const nonScorableBids = [];
  const conditionalBids = [];
  const nonConditionalBids = [];

  for (const p of bids) {
    if (isOptionLevelIsSmallerEqualThanReserve(p.optionLevel)) {
      p.score = 0;
      conditionalBids.push(p);
    } else if (!p.scoreApplicable) {
      nonScorableBids.push(p);
    } else if (p.optionLevel === RESERVE_DOWN_TO.levelNonReserveDownTo) {
      nonConditionalBids.push(p);
    }
  }
  conditionalBids.sort((a, b) => a.optionLevel - b.optionLevel);
  nonConditionalBids.sort((a, b) => a.score - b.score);

  return [...nonScorableBids, ...conditionalBids, ...nonConditionalBids];
}

export function getTotalCredits(initialCredit, activities) {
  let creditSum = initialCredit;
  if (activities && activities.entries.length > 0) {
    const activitiesWithoutReserveAsParentFilter = (activity) => {
      if (activity.addedByUser) {
        return true;
      }
      return activity.type !== 'RESERVE';
    };
    creditSum += activities.entries
      .filter(activitiesWithoutReserveAsParentFilter)
      .reduce((a, b) => ({ credit: a.credit + b.credit })).credit;
  }

  return formatMinutesToHoursWithMinutes(creditSum);
}

export function adjustBidPreferences(component, bidPreferences, preference) {
  component.setState(state => ({
    bidPreferences: { ...state.bidPreferences, entries: preference }
  }));
}

export function requestWeightSorting(component, sort) {
  const { bidPreferences } = component.state;
  const bidMode = (component.state.bidmode === undefined) ? component.props.bidSession.bidMode : component.state.bidmode;

  function sortBidsByCategory(preferences, ascending = true) {
    // Separate into three categories
    const noScoreBids = preferences.filter(p => !p.scoreApplicable);
    const conditionalBids = preferences.filter(p => p.scoreApplicable !== false && isOptionLevelIsSmallerEqualThanReserve(p.optionLevel));
    const nonConditionalBids = preferences.filter(p => p.scoreApplicable !== false && p.optionLevel === OPTION_LEVEL_WEIGHTED_PREFERENCE);

    // Sort each category
    if (ascending) {
      conditionalBids.sort((a, b) => a.optionLevel - b.optionLevel);
      nonConditionalBids.sort((a, b) => parseFloat(a.score) - parseFloat(b.score));
    } else {
      conditionalBids.sort((a, b) => b.optionLevel - a.optionLevel);
      nonConditionalBids.sort((a, b) => parseFloat(b.score) - parseFloat(a.score));
    }
    // Combine in order: non-scoreable, conditional, non-conditional
    return [...noScoreBids, ...conditionalBids, ...nonConditionalBids];
  }

  if (bidMode !== BidMode.MANUAL.biddingMode) {
    const preference = bidPreferences.entries;
    if (sort) {
      bidPreferences.entries = sortBidsByCategory(preference, true);
    } else {
      bidPreferences.entries = sortBidsByCategory(preference, false);
    }
    adjustBidPreferences(component, bidPreferences, bidPreferences.entries);
  } else {
    const bidMode = getBidMode();
    if (bidMode === BidMode.AUTOMATIC.biddingMode && sort === true) {
      bidPreferences.entries = sortBidsByCategory(bidPreferences.entries, false);
    } else if (bidMode === BidMode.AUTOMATIC.biddingMode && sort === false) {
      bidPreferences.entries = sortBidsByCategory(bidPreferences.entries, true);
    } else if (bidMode !== BidMode.AUTOMATIC.biddingMode && sort === true) {
      bidPreferences.entries = sortBidsByCategory(bidPreferences.entries, true);
    } else if (bidMode !== BidMode.AUTOMATIC.biddingMode && sort === false) {
      bidPreferences.entries = sortBidsByCategory(bidPreferences.entries, false);
    }
    adjustBidPreferences(component, bidPreferences, bidPreferences.entries);
  }
}

// debounce of 1 sec of the api call on weigth changes
const debouncedHandleInputChange = debounce((component, preference, weight, bidmode, prefIds) => {
  component.props.updateWeight(preference, weight, bidmode, prefIds).then((response) => {
  }).catch((e) => {
    console.log(e.statusText);
    component.setState(state => ({ ...state, isSaving: false }));
  });
}, DEBOUNCE_DELAY);

export function requestUpdateWeight(component, bidPreferences, bidId, weight, preference, bidmode, prefIds) {
  component.preferenceHasChange = !component.preferenceHasChange;
  const newPreference = bidPreferences.entries;
  adjustBidPreferences(component, bidPreferences, newPreference);
  debouncedHandleInputChange(component, preference, weight, bidmode, prefIds)
}

export function bidSessionUpdateWeight(component, bidPreferences, bidId, weight, preference, bidmode) {
  let prefIds = []
  if (preference.groupName) {
    bidPreferences.entries.forEach((Pref) => {
      if (Pref.groupName === preference.groupName) {
        Pref.score = weight;
        prefIds.push(Pref.bidAggregateId)
      }
    })
  } else {
    bidPreferences.entries.forEach((Pref) => {
      if (Pref.bidAggregateId === bidId) {
        Pref.score = weight;
        prefIds.push(Pref.bidAggregateId)
      }
    });
  }
  if (weight === '') {
    component.preferenceHasChange = !component.preferenceHasChange;
    const newPreference = bidPreferences.entries;
    adjustBidPreferences(component, bidPreferences, newPreference);
    return;
  }

  const bidMode = getBidMode();
  if (bidMode === BidMode.AUTOMATIC.biddingMode && (weight > 0 && weight < 4)) {
    requestUpdateWeight(component, bidPreferences, bidId, weight, preference, bidmode, prefIds);
  } else if ((bidMode !== BidMode.AUTOMATIC.biddingMode) && (weight > 0 && weight < 1001)) {
    requestUpdateWeight(component, bidPreferences, bidId, weight, preference, bidmode, prefIds);
  }
}

export function getBidWeightLabel(weight) {
  const WEIGTH_MODE = getLabels().WEIGTH_MODE;
  const Weight_Label = [{ value: 1, label: WEIGTH_MODE.HIGH }, { value: 2, label: WEIGTH_MODE.MEDIUM }, { value: 3, label: WEIGTH_MODE.LOW }];
  if (weight != '' && weight != null) {
    let weight_label = weight <= Weight_Label.length ? Weight_Label.filter((e) => e.value == weight)[0].label : '';
    return weight_label;
  }
  return '';
}

export function transformPreferences(preferences) {
  const groupedPreferences = {};

  // Regrouper les préférences par groupSequence
  preferences.forEach(pref => {
    if (!groupedPreferences[pref.groupSequence]) {
      groupedPreferences[pref.groupSequence] = {
        groupSequence: pref.groupSequence,
        preferences: [],
        importable: true, // Par défaut, un groupe est importable
      };
    }

    groupedPreferences[pref.groupSequence].preferences.push(pref);

    // Si une préférence dans le groupe n'est pas importable, alors le groupe est marqué comme non importable
    if (!pref.importable) {
      groupedPreferences[pref.groupSequence].importable = false;
    }
  });

  // Créer la structure des résultats
  const results = Object.values(groupedPreferences).map(group => ({
    groupSequence: group.groupSequence,
    preferences: group.preferences,
    importable: group.importable,
  }));

  // Déterminer si une préférence combinée est importable ou non
  results.forEach(group => {
    group.preferences.forEach(pref => {
      if (pref.groupName) { // Vérifier si la préférence est combinée
        const groupImportable = results
          .filter(g => g.preferences.some(p => p.groupName === pref.groupName))
          .every(g => g.importable);

        pref.importable = groupImportable;
      }
    });
  });

  return results;
}

export const processPreferences = (type, oldScore, preferences, showNotification) => {
  const PREFERENCE_MENU = getLabels().PREFERENCE_MENU;

  let lastBidScopeUsed = getLastBidScopeUsed();

  let preferencesToCheck = preferences.filter(
    preference => preference.preferenceOptionPropertyId === lastBidScopeUsed
  );

  let higherScore = Math.max(...preferencesToCheck.map(preference => preference.score), 0);

  if (type === 'up' && oldScore === 1) {
    showNotification(PREFERENCE_MENU?.CANNOT_MOVE_UP || 'You cannot move this bid up');
    return null;
  } else if (type === 'down' && higherScore === oldScore) {
    showNotification(PREFERENCE_MENU?.CANNOT_MOVE_DOWN || 'You cannot move this bid down');
    return null;
  }

  return preferencesToCheck;
};

export const getLastBidScopeUsed = function () {
  return sessionStorage.getItem(LAST_BID_SCOPE_USE) === RESERVE_BID_SCOPE ? 15 : 0;
}

export const getStaffId = function () {
  return sessionStorage.getItem(STAFFID);
}

export function isOptionLevelIsSmallerEqualThanReserve(optionLevel) {
  return optionLevel <= RESERVE_DOWN_TO.levelAny;
}

export function isOptionLevelIsBiggerEqualThanReserve(optionLevel) {
  return optionLevel >= RESERVE_DOWN_TO.levelAny;
}
